/**
 * F36 (Wave 2E) — Admin club detail + moderation page.
 *
 * Route: /clubs/:clubId
 * Permission: clubs:read (super_admin + support + content_moderator)
 *
 * Moderation actions ([Suspend] / [Reinstate]) are additionally gated
 * client-side on `clubs:moderate` (super_admin + content_moderator); the
 * server re-enforces.
 *
 * F09 v2 retired the F36 "Verify club" action: the club tick is an F09 request.
 * The header shows the inert tick from `club.verified`, and the Verification
 * card deep-links to `/verifications/entities/club/:id` (needs
 * `verifications:read`).
 *
 * Composition (mirrors the F29 AdminTeamDetailPage):
 *   - Header: title + tick + status chip; Verification card; suspended banner
 *   - KPI strip: teams / trainers / members
 *   - Action bar: state-aware Suspend / Reinstate
 *   - Tabs: Roster (members + assigned trainers) / Teams (club-managed) / Audit
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  AuditLogTimeline,
  type AuditEvent,
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Skeleton,
  Stat,
  StatusChip,
  type StatusChipStatus,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { B2bAccountMemberRole, B2bAuditEntry, ClubDetail } from '@bravemove/types';
import {
  adminClubsApi,
  type AdminClubDetailResponse,
  type AdminClubRosterMember,
  type AdminClubTeam,
  type ReinstateClubInput,
  type SuspendClubInput,
} from '../../api/clubs.js';
import { translateApiError } from '../../auth/errors.js';
import { useAuth } from '../../auth/useAuth.js';
import { SuspendClubDialog } from '../../components/clubs/SuspendClubDialog.js';
import { ReinstateClubDialog } from '../../components/clubs/ReinstateClubDialog.js';
import {
  EntityVerificationCard,
  InertTick,
} from '../../components/verification/EntityVerificationCard';

const STATUS_CHIP_MAP: Record<ClubDetail['status'], StatusChipStatus> = {
  active: 'approved',
  suspended: 'in_review',
  disabled: 'rejected',
};

const TEAM_STATUS_CHIP_MAP: Record<string, StatusChipStatus> = {
  active: 'approved',
  suspended: 'in_review',
  disbanded: 'rejected',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

/** Map a B2bAuditEntry → AuditLogTimeline AuditEvent. */
function mapAuditEntry(
  e: B2bAuditEntry,
  // Match i18next's `t` shape so call sites pass `t` directly.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any,
  locale: string,
): AuditEvent {
  const action = e.action;
  let kind: AuditEvent['kind'] = 'info';
  if (action.includes('cancelled') || action.includes('deleted') || action.includes('unassigned')) {
    kind = 'delete';
  } else if (
    action.includes('created') ||
    action.includes('assigned') ||
    action.includes('accepted')
  ) {
    kind = 'create';
  } else if (
    action.includes('updated') ||
    action.includes('rescheduled') ||
    action.includes('marked')
  ) {
    kind = 'update';
  }

  const actorName =
    e.actorAdminId !== null
      ? t('admin:club.detail.audit_actor_admin')
      : e.surface === 'system'
        ? t('admin:club.detail.audit_actor_system')
        : ((e.payload as { actorDisplayName?: string }).actorDisplayName ?? e.actorUserId ?? '—');

  const event: AuditEvent = {
    id: e.id,
    at: formatDate(e.createdAt, locale),
    kind,
    summary: t(`club:audit_action.${e.action}`, { defaultValue: e.action }),
    actor: { name: actorName },
  };
  if (e.targetKind) {
    return { ...event, targetLabel: e.targetKind };
  }
  return event;
}

function roleLabel(
  role: B2bAccountMemberRole,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any,
): string {
  return t(`admin:club.detail.roster_role.${role}`, { defaultValue: role });
}

function RosterRow({
  member,
  t,
}: {
  member: AdminClubRosterMember;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}) {
  return (
    <li className="flex items-center gap-3 py-2.5 border-b border-hairline last:border-b-0">
      <Avatar
        name={member.displayName}
        src={member.avatarUrl ?? undefined}
        size="sm"
        status={
          member.status === 'active' ? 'online' : member.status === 'suspended' ? 'busy' : undefined
        }
      />
      <div className="flex-1 min-w-0">
        <span className="block text-[13px] font-medium text-ink truncate">
          {member.displayName}
        </span>
        {member.email ? (
          <span className="block text-[11.5px] text-ink-3 font-mono truncate">{member.email}</span>
        ) : null}
      </div>
      <Badge tone={member.role === 'owner' ? 'accent' : 'neutral'} size="sm">
        {roleLabel(member.role, t)}
      </Badge>
    </li>
  );
}

export function AdminClubDetailPage() {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const toast = useToast();
  const auth = useAuth();

  const canModerate = useMemo(
    () => auth.adminUser?.permissions.includes('clubs:moderate') ?? false,
    [auth.adminUser],
  );

  const [detail, setDetail] = useState<AdminClubDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reinstateOpen, setReinstateOpen] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminClubsApi.detail(clubId);
      if (!data) {
        setError(t('admin:club.detail.error_not_found'));
        setDetail(null);
      } else {
        setDetail(data);
      }
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [clubId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const handleSuspend = async (body: SuspendClubInput) => {
    if (!clubId || !detail) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminClubsApi.suspend(clubId, body);
      toast.push({
        tone: 'success',
        title: t('admin:club.suspend_dialog.toast_success', {
          clubName: detail.club.displayName,
        }),
      });
      setSuspendOpen(false);
      await fetchDetail();
    } catch (err) {
      setActionError(translateApiError(err, t));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReinstate = async (body: ReinstateClubInput) => {
    if (!clubId || !detail) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminClubsApi.reinstate(clubId, body);
      toast.push({
        tone: 'success',
        title: t('admin:club.reinstate_dialog.toast_success', {
          clubName: detail.club.displayName,
        }),
      });
      setReinstateOpen(false);
      await fetchDetail();
    } catch (err) {
      setActionError(translateApiError(err, t));
    } finally {
      setActionLoading(false);
    }
  };

  const teamColumns = useMemo<readonly TableColumn<AdminClubTeam>[]>(
    () => [
      {
        key: 'name',
        header: t('admin:club.detail.teams_column_name'),
        cell: (row) => <span className="text-[13px] font-medium text-ink">{row.name}</span>,
      },
      {
        key: 'sport',
        header: t('admin:club.detail.teams_column_sport'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">
            {t(`sports:${row.sportName}`, { defaultValue: row.sportName })}
          </span>
        ),
      },
      {
        key: 'members',
        header: t('admin:club.detail.teams_column_members'),
        cell: (row) => (
          <span className="tabular-nums text-[12.5px] text-ink">{row.memberCount}</span>
        ),
      },
      {
        key: 'trainers',
        header: t('admin:club.detail.teams_column_trainers'),
        cell: (row) => (
          <span className="tabular-nums text-[12.5px] text-ink">{row.assignedTrainerCount}</span>
        ),
      },
      {
        key: 'status',
        header: t('admin:club.detail.teams_column_status'),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={TEAM_STATUS_CHIP_MAP[row.status] ?? 'approved'}
            label={t(`team:status.${row.status}`, { defaultValue: row.status })}
          />
        ),
      },
    ],
    [t],
  );

  if (loading) {
    return (
      <div className="space-y-4" data-testid="club-detail-loading">
        <Skeleton className="h-8 w-48 rounded-md" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/clubs')}>
          ← {t('admin:club.detail.back_to_queue')}
        </Button>
        <EmptyState
          icon={<UIIcon name="team" size={36} color="currentColor" />}
          title={t('admin:club.detail.error_title')}
          description={error ?? ''}
        />
      </div>
    );
  }

  const {
    club,
    verifiedAt,
    suspendedReason,
    suspendedAt,
    suspendedByAdminDisplayName,
    members,
    teams,
    recentAuditEntries,
  } = detail;
  const isActive = club.status === 'active';
  const isSuspended = club.status === 'suspended';
  const isDisabled = club.status === 'disabled';

  const auditEvents: AuditEvent[] = recentAuditEntries.map((e) => mapAuditEntry(e, t, locale));

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/clubs')}>
          ← {t('admin:club.detail.back_to_queue')}
        </Button>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap rounded-xl border border-hairline bg-surface p-5">
        <div className="flex items-start gap-4 min-w-0">
          <Avatar name={club.displayName} size="lg" />
          <div className="space-y-1 min-w-0">
            <h1 className="text-h1 font-bold text-ink" data-testid="club-detail-title">
              {(() => {
                const raw = t('admin:club.detail.title', {
                  clubName: club.displayName,
                  defaultValue: 'Club: {clubName}',
                }) as string;
                return raw.includes('{clubName}') ? `Club: ${club.displayName}` : raw;
              })()}
            </h1>
            <p className="text-[13px] text-ink-3" data-testid="club-detail-subtitle">
              {(() => {
                const createdAt = formatDate(club.createdAt, locale);
                const raw = t('admin:club.detail.header_subtitle', {
                  createdAt,
                  defaultValue: 'Created on {createdAt}',
                }) as string;
                return raw.includes('{createdAt}') ? `Created on ${createdAt}` : raw;
              })()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <InertTick
            verified={club.verified}
            entityType="club"
            size="md"
            testId="club-detail-tick"
          />
          <StatusChip
            status={STATUS_CHIP_MAP[club.status]}
            label={t(`club:status.${club.status}`)}
            pulse={isSuspended}
          />
        </div>
      </div>

      {/* F09 v2 — the club tick + a deep link into the verifications module
          (the retired "Verify club" button). */}
      <EntityVerificationCard
        entityType="club"
        entityId={club.id}
        verified={club.verified}
        verifiedAt={verifiedAt}
        testId="club-verification-card"
      />

      {/* Suspended banner */}
      {isSuspended ? (
        <div data-testid="suspended-banner">
          <Banner tone="warning" title={t('admin:club.detail.suspended_banner_title')}>
            {(() => {
              const suspendedAtText = formatDate(suspendedAt, locale);
              const reason = suspendedReason ?? '—';
              if (suspendedByAdminDisplayName) {
                const raw = t('admin:club.detail.suspended_banner_body', {
                  adminName: suspendedByAdminDisplayName,
                  suspendedAt: suspendedAtText,
                  reason,
                  defaultValue: 'Suspended by {adminName} on {suspendedAt}. Reason: {reason}',
                }) as string;
                return raw.includes('{adminName}') ||
                  raw.includes('{suspendedAt}') ||
                  raw.includes('{reason}')
                  ? `Suspended by ${suspendedByAdminDisplayName} on ${suspendedAtText}. Reason: ${reason}`
                  : raw;
              }
              const raw = t('admin:club.detail.suspended_banner_body_no_admin', {
                suspendedAt: suspendedAtText,
                reason,
                defaultValue: 'Suspended on {suspendedAt}. Reason: {reason}',
              }) as string;
              return raw.includes('{suspendedAt}') || raw.includes('{reason}')
                ? `Suspended on ${suspendedAtText}. Reason: ${reason}`
                : raw;
            })()}
          </Banner>
        </div>
      ) : null}

      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="club-kpi-strip">
        <Stat
          label={t('admin:club.detail.kpi_teams')}
          value={club.teamCount}
          icon={<UIIcon name="team" size={16} color="currentColor" />}
        />
        <Stat
          label={t('admin:club.detail.kpi_trainers')}
          value={club.trainerCount}
          icon={<UIIcon name="whistle" size={16} color="currentColor" />}
        />
        <Stat
          label={t('admin:club.detail.kpi_members')}
          value={members.length}
          icon={<UIIcon name="user" size={16} color="currentColor" />}
        />
      </div>

      {/* Action bar — gated on `clubs:moderate` */}
      {canModerate && !isDisabled ? (
        <div className="flex flex-wrap gap-2" data-testid="club-action-bar">
          {isActive ? (
            <Button
              variant="destructive"
              onClick={() => {
                setActionError(null);
                setSuspendOpen(true);
              }}
              data-testid="open-suspend-dialog"
            >
              {t('admin:club.detail.action_suspend')}
            </Button>
          ) : null}
          {isSuspended ? (
            <Button
              variant="primary"
              onClick={() => {
                setActionError(null);
                setReinstateOpen(true);
              }}
              data-testid="open-reinstate-dialog"
            >
              {t('admin:club.detail.action_reinstate')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Tabs: Roster / Teams / Audit */}
      <Tabs defaultValue="roster">
        <TabList aria-label={t('admin:club.detail.section_overview')}>
          <Tab value="roster">{t('admin:club.detail.tab_roster')}</Tab>
          <Tab value="teams">{t('admin:club.detail.tab_teams')}</Tab>
          <Tab value="audit">{t('admin:club.detail.tab_audit')}</Tab>
        </TabList>

        <TabPanel value="roster">
          <Card>
            {members.length === 0 ? (
              <p className="text-[12.5px] text-ink-3">{t('admin:club.detail.roster_empty')}</p>
            ) : (
              <ul className="m-0 p-0 list-none" data-testid="club-roster-list">
                {members.map((m) => (
                  <RosterRow key={m.id} member={m} t={t} />
                ))}
              </ul>
            )}
          </Card>
        </TabPanel>

        <TabPanel value="teams">
          {teams.length === 0 ? (
            <EmptyState
              icon={<UIIcon name="team" size={32} color="currentColor" />}
              title={t('admin:club.detail.teams_empty')}
            />
          ) : (
            <Table
              data={teams as AdminClubTeam[]}
              columns={teamColumns as TableColumn<AdminClubTeam>[]}
              rowKey={(row) => row.id}
              onRowPress={(row) => navigate(`/teams/${row.id}`)}
              empty={t('admin:club.detail.teams_empty')}
            />
          )}
        </TabPanel>

        <TabPanel value="audit">
          <Card>
            {auditEvents.length === 0 ? (
              <p className="text-[12.5px] text-ink-3">{t('admin:club.detail.audit_empty')}</p>
            ) : (
              <AuditLogTimeline events={auditEvents} />
            )}
          </Card>
        </TabPanel>
      </Tabs>

      {/* Dialogs */}
      <SuspendClubDialog
        open={suspendOpen}
        clubName={club.displayName}
        loading={actionLoading}
        serverError={actionError}
        onClose={() => setSuspendOpen(false)}
        onSubmit={handleSuspend}
      />
      <ReinstateClubDialog
        open={reinstateOpen}
        clubName={club.displayName}
        loading={actionLoading}
        serverError={actionError}
        onClose={() => setReinstateOpen(false)}
        onSubmit={handleReinstate}
      />
    </div>
  );
}
